/**
 * A collection's complete product feed, for matching in the browser.
 *
 * Shopify's search cannot be scoped to a collection and a collection cannot
 * take a keyword, so anything that answers "which of this machine's parts
 * match what you typed" needs the machine's products in hand. This loads
 * every page of /collections/{handle}/products.json (250 a page), trims each
 * product to what matching and a card need, and keeps the result for ten
 * minutes per tab. One load at a time per collection: a second caller joins
 * the first request rather than starting another.
 *
 * Used by the header's predictive search and the search page's machine scope.
 */
(function () {
  'use strict';

  const PAGE_SIZE = 250;
  const MAX_PAGES = 20;
  const CACHE_PREFIX = 'fmt:collection-feed:v2:'; // v2: options and variant values (#74)
  const CACHE_TTL = 10 * 60 * 1000;
  const memory = new Map();
  const inflight = new Map();

  function cacheGet(key) {
    if (memory.has(key)) return memory.get(key);
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const { at, products } = JSON.parse(raw);
      if (Date.now() - at >= CACHE_TTL) return null;
      memory.set(key, products);
      return products;
    } catch { return null; }
  }

  function cacheSet(key, products) {
    memory.set(key, products);
    try { sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ at: Date.now(), products })); } catch { /* full or blocked */ }
  }

  function trim(p) {
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const options = (Array.isArray(p.options) ? p.options : [])
      .filter((o) => o && o.name && o.name !== 'Title')
      .map((o) => ({ name: o.name, position: o.position, values: o.values || [] }));
    return {
      handle: p.handle,
      title: p.title,
      vendor: p.vendor,
      product_type: p.product_type,
      tags: (p.tags || []).filter((t) => /^ZX[23]_/.test(t)),
      image: p.images && p.images[0] ? p.images[0].src : null,
      options,
      variants: variants.map((v) => ({
        id: v.id, sku: v.sku, price: v.price, compare_at_price: v.compare_at_price, available: v.available,
        options: options.length ? [v.option1, v.option2, v.option3] : undefined,
        image: v.featured_image && v.featured_image.src ? v.featured_image.src : null,
      })),
    };
  }

  async function fetchAll(key, signal) {
    const all = [];
    const seen = new Set();
    for (let page = 1; page <= MAX_PAGES; page++) {
      const res = await fetch(`/collections/${encodeURIComponent(key)}/products.json?limit=${PAGE_SIZE}&page=${page}`, { signal, credentials: 'omit' });
      if (!res.ok) throw new Error(`collection feed ${res.status}`);
      const products = (await res.json()).products || [];
      let added = 0;
      for (const p of products) {
        if (!p || !p.handle || seen.has(p.handle)) continue;
        seen.add(p.handle);
        all.push(trim(p));
        added++;
      }
      // A short page is the last page; a page that adds nothing is a repeat.
      if (products.length < PAGE_SIZE || added === 0) break;
    }
    return all;
  }

  /** Resolves to the trimmed products of a collection. */
  function load(key, { signal } = {}) {
    const cached = cacheGet(key);
    if (cached) return Promise.resolve(cached);
    if (inflight.has(key)) return inflight.get(key);
    const promise = fetchAll(key, signal)
      .then((products) => { cacheSet(key, products); return products; })
      .finally(() => inflight.delete(key));
    inflight.set(key, promise);
    return promise;
  }

  /** What is already in hand, without fetching. */
  function peek(key) {
    return cacheGet(key);
  }

  /** The words a customer could type to find a product: title, part number, brand, type. */
  function words(product) {
    const variant = (product.variants && product.variants[0]) || {};
    const typeTags = (product.tags || []).filter((t) => /^ZX[23]_/.test(t)).map((t) => t.slice(4));
    return [product.title, variant.sku, product.vendor, product.product_type, ...typeTags]
      .join(' ')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  /**
   * Does every token start a word? Prefix matching, not substring: "oil" must
   * not find "Foil", and the half-typed "f" in "oil f" should find "Filter"
   * without finding every word that contains an f.
   */
  function matches(product, tokens) {
    if (!tokens.length) return true;
    const ws = product._words || (product._words = words(product));
    return tokens.every((t) => ws.some((w) => w.startsWith(t)));
  }

  /** How many of the tokens start a word — for loosening a search that matches nothing as a whole. */
  function score(product, tokens) {
    const ws = product._words || (product._words = words(product));
    return tokens.filter((t) => ws.some((w) => w.startsWith(t))).length;
  }

  function tokens(terms) {
    return String(terms || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  }

  /**
   * In-stock counts over a feed: the total, how many are in stock, and the
   * in-stock count per category (ZX2) and per Accessories product type (ZX3).
   * Liquid has no in-stock count; the feed does, so the machine's menu and
   * page count what a customer can actually buy.
   */
  function stock(products) {
    const out = { total: products.length, inStock: 0, lvl2: new Map(), lvl3: new Map() };
    for (const p of products) {
      if (!(p.variants || []).some((v) => v.available)) continue;
      out.inStock++;
      const lvl2 = (p.tags || []).filter((t) => t.startsWith('ZX2_')).map((t) => t.slice(4));
      const lvl3 = (p.tags || []).filter((t) => t.startsWith('ZX3_')).map((t) => t.slice(4));
      for (const c of lvl2) out.lvl2.set(c, (out.lvl2.get(c) || 0) + 1);
      if (lvl2.includes('Accessories')) for (const t of lvl3) out.lvl3.set(t, (out.lvl3.get(t) || 0) + 1);
    }
    return out;
  }

  window.CollectionFeed = { load, peek, matches, score, tokens, words, stock };
})();
